const _excluded = ["value", "className", "placeholder", "debounceTime", "registerHotkey", "showHint", "variant", "fluidWidth", "autoFocus", "onChange", "onClear", "onInputBlur", "onInputFocus", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash-es';
import { Search20, Search24 } from "../../icons";
import { CloseButton } from "../CloseButton";
import { useOverlayContext } from "../OverlayContext";
import styles from "./Searchbox.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Searchbox(props) {
  const {
      value,
      className,
      placeholder,
      debounceTime,
      registerHotkey = false,
      showHint = false,
      variant = 'chip',
      fluidWidth,
      autoFocus,
      onChange,
      onClear,
      onInputBlur,
      onInputFocus,
      testId
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [inputValue, setInputValue] = useState(value || '');
  const {
    hasOpenModals
  } = useOverlayContext();
  const inputValueBeforeDebounce = useRef(inputValue);
  const debouncedOnChange = useMemo(() => {
    return debounce(event => {
      if (inputValueBeforeDebounce.current !== event.target.value) {
        onChangeRef.current(event);
      }
    }, debounceTime);
  }, [debounceTime]);
  const wrappedOnChange = event => {
    inputValueBeforeDebounce.current = event.target.value;
    onChange && onChange(event);
  };
  const onChangeRef = useRef(wrappedOnChange);
  onChangeRef.current = wrappedOnChange;
  const debouncedOnChangeRef = useRef(debouncedOnChange);
  debouncedOnChangeRef.current = debouncedOnChange;
  const searchBoxRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHotkeyRegistered, setIsHotkeyRegistered] = useState(false);
  useEffect(() => {
    function onKeyDown(ev) {
      if (ev.key === '/') {
        ev.preventDefault();
        searchBoxRef.current != null && searchBoxRef.current.focus();
      }
    }
    if (registerHotkey && !isFocused && !hasOpenModals) {
      document.addEventListener('keydown', onKeyDown, true);
      setIsHotkeyRegistered(true);
      return () => {
        document.removeEventListener('keydown', onKeyDown, true);
        setIsHotkeyRegistered(false);
      };
    }
  }, [registerHotkey, isFocused, hasOpenModals]);
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);
  useEffect(() => {
    return () => {
      debouncedOnChangeRef.current.cancel();
    };
  }, []);
  function renderSearchIcon() {
    switch (variant) {
      case 'chip':
        return /*#__PURE__*/_jsx(Search20, {
          className: styles.smallSearchIcon
        });
      case 'textfield':
        return /*#__PURE__*/_jsx(Search24, {
          className: styles.searchIcon
        });
    }
  }
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({
    className: cn(styles.root, {
      [styles.textfield]: variant === 'textfield'
    }, {
      [styles.fluidWidth]: fluidWidth
    }, className),
    "data-testid": testId
  }, rest), {}, {
    children: [renderSearchIcon(), /*#__PURE__*/_jsx("input", {
      ref: searchBoxRef,
      type: "text",
      autoFocus: autoFocus,
      className: cn(styles.input, inputValue.length > 0 ? styles.notEmpty : null),
      value: inputValue,
      placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : 'Search...',
      "data-testid": testId && "".concat(testId, "-input"),
      "data-hotkey-registered": isHotkeyRegistered,
      onChange: event => {
        setInputValue(event.target.value);
        if (debounceTime) {
          event.persist();
          debouncedOnChange(event);
        } else {
          onChangeRef.current(event);
        }
      },
      onFocus: ev => {
        setIsFocused(true);
        onInputFocus === null || onInputFocus === void 0 || onInputFocus(ev);
      },
      onBlur: ev => {
        setIsFocused(false);
        onInputBlur === null || onInputBlur === void 0 || onInputBlur(ev);
      }
    }), registerHotkey && showHint && /*#__PURE__*/_jsx("div", {
      className: styles.hotkeyHint,
      children: /*#__PURE__*/_jsx("kbd", {
        children: "/"
      })
    }), !!inputValue && /*#__PURE__*/_jsx(CloseButton, {
      className: styles.clear,
      size: "small",
      testId: testId && "".concat(testId, "-clear"),
      onClick: () => {
        // In uncontrolled mode, clear the value automatically.
        if (typeof value !== 'string') {
          setInputValue('');
        }
        onClear && onClear();
      }
    })]
  }));
}